import React, { useContext, useEffect, useReducer } from 'react';
import { changePortionPositionReducer } from './changePortionPositionReducer';
import { ChangePortionPositionActionTypes } from './changePortionPositionActions';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	ChangePortionPositionContext,
	changePortionPositionInitialState,
	TChangePortionPositionSelectedRestaurant,
	TTypeOfChangePortionPositionModalWindow,
} from './changePortionPositionContext';
import { IGetPortionPositionList, IGetPortionPositionListArguments, IPortionPositionItem } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getPortionPositionList } from 'backend/dishes';

export const ChangePortionPositionState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(changePortionPositionReducer, changePortionPositionInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { response, loading, refetch } = useFetchData<IGetPortionPositionList, IGetPortionPositionListArguments>(
		state.IDSelectedDish ? getPortionPositionList : null,
		{
			restaurantId: state.selectedRestaurant?.id || '',
			dishId: state.IDSelectedDish || '',
		},
	);

	useEffect(() => {
		if (response) {
			dispatch({
				type: ChangePortionPositionActionTypes.SET_POSITIONS_DATA,
				payload: {
					positionsData: response.data,
				},
			});
		}
	}, [response]);

	useEffect(() => {
		refetch();
	}, [state.IDSelectedDish]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const setIDCustomer = (value: Nullable<string>) => {
		dispatch({
			type: ChangePortionPositionActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setRestaurant = (value: Nullable<TChangePortionPositionSelectedRestaurant>) => {
		dispatch({
			type: ChangePortionPositionActionTypes.SET_RESTAURANT,
			payload: {
				selectedRestaurant: value,
			},
		});
	};

	const setIDDish = (value: Nullable<string>) => {
		dispatch({
			type: ChangePortionPositionActionTypes.SET_ID_DISH,
			payload: {
				IDSelectedDish: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: ChangePortionPositionActionTypes.RESET_SELECTED_CONTROLS });

	const setPositionsData = (value: IPortionPositionItem[]) => {
		dispatch({
			type: ChangePortionPositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: value,
			},
		});
	};

	const reorderItems = (value: IPortionPositionItem[]) => {
		dispatch({
			type: ChangePortionPositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfChangePortionPositionModalWindow) => {
		dispatch({
			type: ChangePortionPositionActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setVisibleAdditionalRestaurantsModal = (value: boolean) => {
		dispatch({
			type: ChangePortionPositionActionTypes.SET_VISIBLE_ADDITIONAL_RESTAURANTS_MODAL,
			payload: {
				isVisibleAdditionalRestaurantsModal: value,
				isApplyToOne: value ? false : state.isApplyToOne,
				selectedAdditionalRestaurants: value ? [] : state.selectedAdditionalRestaurants,
				selectSearchString: value ? '' : state.selectSearchString,
			},
		});
	};

	const toggleApplyToOne = () => {
		dispatch({
			type: ChangePortionPositionActionTypes.TOGGLE_APPLY_TO_ONE,
		});
	};

	const setAdditionalRestaurants = (value: TChangePortionPositionSelectedRestaurant[]) => {
		dispatch({
			type: ChangePortionPositionActionTypes.SET_ADDITIONAL_RESTAURANTS,
			payload: {
				selectedAdditionalRestaurants: value,
			},
		});
	};

	const changeSelectSearchString = (value: string) => {
		dispatch({
			type: ChangePortionPositionActionTypes.CHANGE_SELECT_SEARCH_STRING,
			payload: {
				selectSearchString: value,
			},
		});
	};

	return (
		<ChangePortionPositionContext.Provider
			value={{
				setIDCustomer,
				setRestaurant,
				setIDDish,
				resetSelectedControls,
				setPositionsData,
				reorderItems,
				changeTypeOfModalWindow,
				setVisibleAdditionalRestaurantsModal,
				toggleApplyToOne,
				setAdditionalRestaurants,
				changeSelectSearchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				selectedRestaurant: state.selectedRestaurant,
				IDSelectedDish: state.IDSelectedDish,
				positionsData: state.positionsData,
				typeOfModalWindow: state.typeOfModalWindow,
				isVisibleAdditionalRestaurantsModal: state.isVisibleAdditionalRestaurantsModal,
				isApplyToOne: state.isApplyToOne,
				selectedAdditionalRestaurants: state.selectedAdditionalRestaurants,
				selectSearchString: state.selectSearchString,
			}}>
			{children}
		</ChangePortionPositionContext.Provider>
	);
};
